/** Plain-word labels for core enums (UI copy only; no rules live here). */
import type { Clearance, OnboardingStep, OrgKind, OrgSegment, PermitType, Role, Severity } from "@logwave/core";
import { appWords, countiesOf, departmentFor, packFor, permitsForPlace, type UsState } from "@logwave/core";

/** The four kinds the setup wizard offers. Core's OrgKind has three, so food trucks/events map to "restaurant" (see README). */
export const KIND_CHOICES: { id: string; label: string; hint: string; kind: OrgKind; stage: number }[] = [
  { id: "coffee", label: "Coffee shop", hint: "Drinks, pastries, maybe light food", kind: "coffee_shop", stage: 1 },
  { id: "restaurant", label: "Restaurant", hint: "A kitchen that cooks meals", kind: "restaurant", stage: 4 },
  { id: "truck", label: "Food truck or events", hint: "Mobile, or food at events", kind: "restaurant", stage: 3 },
  { id: "community", label: "Church or community kitchen", hint: "Parish hall, meetinghouse, shared kitchen", kind: "community_space", stage: 1 },
];

export const KIND_LABEL: Record<OrgKind, string> = { coffee_shop: "Coffee shop", restaurant: "Restaurant", community_space: "Church or community kitchen", home: "Home" };

export const SEGMENT_CHOICES: { id: OrgSegment; label: string }[] = [
  { id: "lds", label: "Latter-day Saint" },
  { id: "catholic", label: "Catholic" },
  { id: "other_faith", label: "Another faith" },
  { id: "secular", label: "Not faith-based" },
];

/** The permit choices with the state's plain words (a slot the state has no words for gets the generic line). */
export function permitChoicesFor(state?: UsState, county?: string | null): { id: PermitType; label: string; plain: string }[] {
  const w = appWords(state);
  return [
    { id: "food_establishment", label: "Regular food permit", plain: "A shop, restaurant or kitchen open to the public, with a permit from the county health department." },
    { id: "temporary_event", label: "Temporary event permit", plain: "Food for the public at a fair, festival or one-time event. The county gives this permit." },
    { id: "mehk", label: "Home kitchen permit", plain: w.mehkPermitPlain ?? "You make and sell food from your own home kitchen." },
    { id: "private_members_only", label: "Members only", plain: "Food only for your members at private gatherings, not open to the public." },
    { id: "mobile_food_business", label: "Food truck permit", plain: w.mobilePermitPlain ?? "A food truck or cart." },
  ].filter((p) => permitsForPlace(state, county).includes(p.id as PermitType)) as { id: PermitType; label: string; plain: string }[];
}
export const PERMIT_CHOICES: { id: PermitType; label: string; plain: string }[] = permitChoicesFor("UT");
export const PERMIT_LABEL = Object.fromEntries(PERMIT_CHOICES.map((p) => [p.id, p.label])) as Record<PermitType, string>;

export const ROLE_LABEL: Record<Role, string> = { owner: "Owner", admin: "Admin", manager: "Manager", staff: "Staff", setup: "Setup" };
export const ROLE_RANK: Record<Role, number> = { staff: 0, manager: 1, admin: 2, owner: 3, setup: -1 };

export const CLEARANCE_LABEL: Record<Clearance, string> = {
  not_cleared: "Not cleared yet",
  members_only_events: "Cleared for members-only events",
  public_food_work: "Cleared for food work",
};

export const STEP_LABEL: Record<OnboardingStep, string> = {
  invited: "Invited",
  signed_in: "Signed in",
  lessons: "Lessons",
  acknowledgements: "Signing",
  food_handler_course: "State course",
  credential_uploaded: "Permit",
  manager_approved: "Manager OK",
};

export const SEVERITY_GROUP: Record<"urgent" | "high" | "normal", string> = { urgent: "Do now", high: "Today", normal: "When you can" };
export const SEVERITY_LABEL: Record<Severity, string> = { urgent: "Urgent", high: "High", normal: "Normal", low: "Low" };

/** Salt Lake County first because it is the example county in docs/source; the rest alphabetical. */
/** Utah counties, Salt Lake first (the most businesses), then A to Z: from core's health department map (localrules.ts). */
export function countiesFor(state?: UsState): string[] {
  const first = appWords(state).exampleCounty;
  const all = [...packFor(state).counties]; // every county of the state, including any no department we read claims
  return first ? [first, ...all.filter((c) => c !== first)] : [...all];
}
export const UTAH_COUNTIES = countiesFor("UT");

/** Starting area names for incident reports; fully editable in setup and Settings. */
export function initialsOf(name: string): string {
  return name.split(/\s+/).filter(Boolean).map((w) => w[0]!.toUpperCase()).join("").slice(0, 3) || "ME";
}

export function prettyKey(key: string): string {
  const s = key.replace(/[_-]+/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * The verb-object label for a check's primary button: exactly what happens when you press it ("Test sanitizer",
 * "Log fridge temps"), never "Log it" or "Open". Falls back to "Fill in <log name>" for rarer logs.
 */
export function checkActionLabel(templateId: string, label = "", title?: string): string {
  const l = label.toLowerCase();
  switch (templateId) {
    case "sanitizer":
      if (l.includes("spray")) return "Check sanitizer spray";
      if (l.includes("bucket")) return "Test sanitizer and bucket";
      return "Test sanitizer";
    case "wiping_cloths": return "Test cloth bucket";
    case "cold_units": return "Log fridge temps";
    case "calibration": return "Calibrate thermometer";
    case "restroom_check": return "Check restroom";
    case "temp_cook_hold": return "Log food temp";
    case "cooling": return "Log cooling temps";
    case "tcs_items": return "Log food temps";
    case "receiving": return "Log a delivery";
    case "pest_check": return l.includes("quick") ? "Quick pest look" : "Pest walk-through";
    case "task_done": return "Mark done";
    case "health_checkin": return "Start check-in";
    default: return title ? `Fill in ${title.replace(/ log$/i, "").toLowerCase()} log` : "";
  }
}

/** The hint under a county picker: the health department that covers it and where its rules show; undefined when we don't know the county. */
export function countyHint(state: UsState | undefined, county: string | null | undefined): string | undefined {
  const dept = county ? departmentFor(state ?? "UT", county) : undefined;
  if (!dept) return undefined;
  const next = appWords(state).countyRulesNext;
  return `Your health department: ${dept.name}.${next ? ` ${next}` : ""}`;
}
